import { useEffect, useRef, useState } from 'react'
import type { BlobUrls } from '@/lib/blobUrls'
import { useBrowserOnline } from '@/hooks/useBrowserOnline'

/**
 * Object URLs for keys, held from a cache while mounted (lib/blobUrls.ts) and
 * released on unmount. `failed` says the last attempt lost one (offline, or the
 * server refused); an attempt is made again when the connection comes back.
 * `broken(key)` is for an <img> that would not draw its URL: the URL is dropped
 * and fetched once more. `load` must be stable (a module-level function).
 */
export const useBlobUrls = (cache: BlobUrls, keys: string[], load: (key: string) => Promise<Blob>) => {
  const online = useBrowserOnline()
  const [urls, setUrls] = useState<Record<string, string>>({})
  // the attempt that failed: a new attempt (other keys, back online, a retry) clears it
  const [failedFor, setFailedFor] = useState('')
  const [round, setRound] = useState(0)
  const retried = useRef(new Set<string>())
  const wanted = keys.join('\n')
  const attempt = `${wanted}|${online}|${round}`
  useEffect(() => {
    let live = true
    const tried = `${wanted}|${online}|${round}`
    const held = (wanted ? wanted.split('\n') : []).map((key) => {
      const h = cache.acquire(key, () => load(key))
      h.url.then(
        (u) => live && setUrls((s) => (s[key] === u ? s : { ...s, [key]: u })),
        () => live && setFailedFor(tried),
      )
      return h
    })
    return () => {
      live = false
      for (const h of held) h.release()
    }
  }, [cache, load, wanted, online, round])
  const broken = (key: string) => {
    if (retried.current.has(key)) return
    retried.current.add(key)
    cache.drop(key)
    setUrls((s) => {
      const next = { ...s }
      delete next[key]
      return next
    })
    setRound((r) => r + 1)
  }
  return { urls, online, failed: failedFor === attempt, broken }
}
